import type { EnvConfig, FirebaseWebConfig } from '../config/env.ts'

/** FirebaseApp name this module owns. Never reuse `[DEFAULT]`. */
export const INTERNAL_APP_NAME = 'ui-internal'

/**
 * Minimal Auth surface createInternalAuth needs. The Firebase Auth instance
 * satisfies this; tests pass a stub.
 */
export interface AuthHandle {
  tenantId: string | null
}

/**
 * Firebase construction functions, injected so the in-memory persistence
 * choice can be asserted without loading the SDK.
 */
export interface CreateAuthDeps {
  getApp: (name: string) => unknown
  initializeApp: (config: FirebaseWebConfig, name: string) => unknown
  initializeAuth: (
    app: unknown,
    options: { persistence: unknown; popupRedirectResolver: unknown }
  ) => AuthHandle
  inMemoryPersistence: unknown
  popupRedirectResolver: unknown
  getAuth: (app: unknown) => AuthHandle
}

function isAlreadyInitialized(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === 'auth/already-initialized'
  )
}

/**
 * Construct Identity Platform auth for this app.
 *
 * Persistence and the popup resolver are set at `initializeAuth` — never
 * `getAuth()` then `setPersistence()`, which can create the IndexedDB
 * store first. The app is named `ui-internal` so a later audience is
 * a second named app, not `getApps()[0]`.
 *
 * @param env - Per-env Firebase config and tenant id.
 * @param deps - Firebase construction functions.
 * @param persistence - Where the refresh token lives. Defaults to memory.
 *   A `/display/` load passes IndexedDB so a display account can be restored.
 * @returns Auth with that persistence and the env's tenant pinned.
 */
export function createInternalAuth(
  env: EnvConfig,
  deps: CreateAuthDeps,
  persistence: unknown = deps.inMemoryPersistence
): AuthHandle {
  let app: unknown
  try {
    app = deps.getApp(INTERNAL_APP_NAME)
  } catch {
    app = deps.initializeApp(env.firebase, INTERNAL_APP_NAME)
  }

  let auth: AuthHandle
  try {
    auth = deps.initializeAuth(app, {
      persistence,
      popupRedirectResolver: deps.popupRedirectResolver
    })
  } catch (error: unknown) {
    // HMR / StrictMode: this named app was already initialized by us.
    if (!isAlreadyInitialized(error)) {
      throw error
    }
    auth = deps.getAuth(app)
  }
  auth.tenantId = env.tenantId

  return auth
}
